import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import { useGetTruckGpsStopsQuery } from "~/shared/api/truck/truck.grpc";
import {
  isKnownGpsStopType,
  MAX_STOP_SPEED_KMPH,
  UNCORROBORATED_GPS_STOP_TYPES,
  type GpsStopType,
} from "./stop-types";

/** A gps_stop flattened into what a map marker actually needs. */
export interface GpsStop {
  id: string;
  lat: number;
  lon: number;
  type: GpsStopType;
  startAt: Date;
  endAt: Date;
  /** Seconds the truck stood still, from meta when present, else the window. */
  durationSeconds: number;
  /** Name of the matched site, if either detector tied the stop to one. */
  siteName?: string;
  /** Location (depot, polygon, …) the stop was matched to, if any. */
  locationId?: bigint;
  averageSpeedKmph: number;
  numGpsPoints: number;
  /**
   * Which detector wrote the row. Not rendered: the legend reads it to tell
   * whether the pickup-location detector covered this window at all, which is
   * what decides the "not classified by site" warning.
   */
  source?: string;
}

export interface UseGpsStopsArgs {
  truckId?: bigint | number | string | null;
  ownerOrganizationId?: bigint | number | string | null;
  from?: Date | null;
  to?: Date | null;
  /** Also fetch the stops no detector tied to a site (UNKNOWN / NO_NEARBY_PL). */
  includeUnmatched?: boolean;
  /** Skip the request entirely, e.g. while the stops layer is toggled off. */
  enabled?: boolean;
}

/**
 * Fetches a truck's GPS dwell events for a time window.
 *
 * The unmatched stops are a separate, much larger result set, so
 * `includeUnmatched` is part of the query key: flipping it refetches rather
 * than filtering client-side.
 */
export function useGpsStops({
  truckId,
  ownerOrganizationId,
  from,
  to,
  includeUnmatched = false,
  enabled = true,
}: UseGpsStopsArgs) {
  const canQuery =
    enabled && truckId != null && ownerOrganizationId != null && Boolean(from) && Boolean(to);

  const { currentData, isFetching, error } = useGetTruckGpsStopsQuery(
    canQuery
      ? {
          id: BigInt(truckId!),
          ownerOrganizationId: BigInt(ownerOrganizationId!),
          fromTo: {
            from: toTimestamp(from!),
            to: toTimestamp(to!),
          },
          includeUnmatched,
        }
      : skipToken,
  );

  // currentData, not data: RTK Query holds the previous result while a new
  // truck/window is in flight, which would draw the last route's stops.
  const stops = useMemo<GpsStop[]>(() => {
    if (!currentData?.gpsStops) return [];

    return currentData.gpsStops.flatMap((raw): GpsStop[] => {
      const startAt = fromTimestamp(raw.gpsStartAt);
      const endAt = fromTimestamp(raw.gpsEndAt);
      // A stop without a window cannot be placed on a timeline; without
      // coordinates it cannot be placed on the map at all.
      if (!startAt || !endAt || raw.lat == null || raw.lon == null) return [];

      const meta = (raw.meta ?? {}) as Record<string, unknown>;
      const rawType = typeof meta.stop_type === "string" ? meta.stop_type : "";
      const type = isKnownGpsStopType(rawType) ? rawType : "UNCLASSIFIED";
      const averageSpeedKmph = raw.averageSpeedKmph ?? 0;

      // Dropped, not hidden: a legend count including rows the map refuses to
      // draw is the same lie as a count of rows never fetched.
      if (UNCORROBORATED_GPS_STOP_TYPES.includes(type) && averageSpeedKmph > MAX_STOP_SPEED_KMPH) {
        return [];
      }

      const metaDuration = Number(meta.duration_seconds);
      const metaName = typeof meta.location_name === "string" ? meta.location_name : undefined;

      return [
        {
          id: String(raw.id),
          lat: raw.lat,
          lon: raw.lon,
          type,
          startAt,
          endAt,
          durationSeconds: Number.isFinite(metaDuration)
            ? metaDuration
            : Math.max(0, Math.round((endAt.getTime() - startAt.getTime()) / 1000)),
          // Every classified stop_type carries the name in meta, which is why the
          // endpoint does not preload the relations. The fallbacks only matter
          // if a future detector version stops writing location_name.
          siteName: metaName ?? raw.pickupLocation?.name ?? raw.location?.name ?? undefined,
          locationId: raw.locationId || undefined,
          averageSpeedKmph,
          numGpsPoints: Number(raw.numGpsPoints ?? 0),
          source: typeof meta.source === "string" ? meta.source : undefined,
        },
      ];
    });
  }, [currentData]);

  return { stops, isFetching, error };
}
